import { useTranslation } from 'react-i18next';
import styles from './LiveBetting.module.css';
import { useCallback, useContext, useEffect, useState } from 'react';
import { LiveBettingFilters, SportIDs } from 'types/enums';
import { SportsBettingRightSide } from 'components/SportsBettingRightSide/SportsBettingRightSide';
import { SlipContext } from 'context/slipContext';
import { LiveBettingContext } from 'context/liveBettingContext';
import { LiveSportEvents } from './LiveSportEvents/LiveSportEvents';
import { getLiveBettingSportName } from 'config/constants';
import { LiveEvent as LiveEventDto } from 'types/LiveEvent';
import { getTotalEelementsCount } from 'helpers/mapHelper';
import { getLiveUpcomingEvents } from 'services/api/sportBetsService';
import { formatDateTime } from 'helpers/dateHelper';
import { MatchOffer } from 'types/MatchOffer';
import { PrematchCatalogContext } from 'context/prematchCatalogContext';
import { UgPager } from 'components/UgPager/UgPager';
import { SportIcon } from 'components/ThemeIcon/SportIcon';
import { sportTranslationKey } from 'helpers/sportI18n';

const UPCOMING_PAGE_SIZE = 50;

// Sport chips: football, basketball, volleyball first, the rest by count.
const UPCOMING_SPORT_ORDER = [
  SportIDs.SOCCER,
  SportIDs.BASKETBALL,
  SportIDs.VOLLEYBALL,
];
const sportChipRank = (sportId: number) => {
  const i = UPCOMING_SPORT_ORDER.indexOf(sportId);
  return i === -1 ? UPCOMING_SPORT_ORDER.length : i;
};

type UpcomingDay = 'today' | 'tomorrow';
const UPCOMING_DAYS: UpcomingDay[] = ['today', 'tomorrow'];
const UPCOMING_DAY_LABELS: Record<UpcomingDay, string> = {
  today: 'today_label',
  tomorrow: 'tomorrow_label',
};

/** Kickoff must be strictly after this unix time (hide events within the next minute). */
function upcomingKickoffLowerBoundSec(): number {
  return Math.floor(Date.now() / 1000) + 60;
}

/** Kickoff window of one day: what is left of today, or all of tomorrow. */
function buildUpcomingQueryWindow(day: UpcomingDay) {
  const endOf = (offsetDays: number) => {
    const d = new Date();
    d.setDate(d.getDate() + offsetDays);
    d.setHours(23, 59, 59, 999);
    return Math.floor(d.getTime() / 1000);
  };
  if (day === 'tomorrow') {
    // The backend takes kickoff > from_time: start right after midnight.
    return { fromSec: endOf(0), toSec: endOf(1) };
  }
  // Exclude kickoff in the next 60s; must match backend `time__gt=from_time`.
  return { fromSec: upcomingKickoffLowerBoundSec(), toSec: endOf(0) };
}

function filterUpcomingByKickoff(ev: MatchOffer): boolean {
  return (
    typeof ev.time === 'number' &&
    Number.isFinite(ev.time) &&
    ev.time > upcomingKickoffLowerBoundSec()
  );
}

/** Map prematch `MatchOffer` into the shape desktop `LiveEvent` expects (sportId, score placeholders, kickoff time label). */
function matchOfferToLiveBettingRow(ev: MatchOffer): LiveEventDto {
  const sportId = ev.league?.sport ?? 0;
  const leagueName = ev.league?.name ?? '';
  const timeLabel =
    typeof ev.time === 'number' && Number.isFinite(ev.time)
      ? formatDateTime(new Date(ev.time * 1000), {
          removeDate: true,
          removeYear: true,
          removeSeconds: true,
        })
      : null;

  return {
    fi: String(ev.fi),
    league: leagueName,
    home: { name: ev.home?.name ?? '' },
    away: { name: ev.away?.name ?? '' },
    homeGoals: 0,
    awayGoals: 0,
    time: timeLabel,
    bets: ev.bets as unknown as LiveEventDto['bets'],
    fullTimeBet: ev.fullTimeBet as unknown as LiveEventDto['fullTimeBet'],
    sportId,
    kickoffUnix: typeof ev.time === 'number' ? ev.time : undefined,
  };
}

export const LiveBetting = () => {
  const [selectedLiveFilter, setSelectedLiveFilter] =
    useState<LiveBettingFilters>(LiveBettingFilters.LIVE_EVENTS);
  const { entries } = useContext(SlipContext);
  const { events, favorites, liveEventsCount, favoritesCount } =
    useContext(LiveBettingContext);
  const { lastPrematchPush } = useContext(PrematchCatalogContext);
  const { t } = useTranslation();

  const [upcomingEvents, setUpcomingEvents] = useState<LiveEventDto[]>([]);
  const [upcomingCount, setUpcomingCount] = useState(0);
  const [upcomingTotalPages, setUpcomingTotalPages] = useState(1);
  const [upcomingPage, setUpcomingPage] = useState(1);
  const [upcomingDay, setUpcomingDay] = useState<UpcomingDay>('today');
  const [upcomingDayCounts, setUpcomingDayCounts] = useState<
    Record<UpcomingDay, number>
  >({ today: 0, tomorrow: 0 });
  // 0 = every sport.
  const [upcomingSport, setUpcomingSport] = useState(0);
  const [upcomingSportCounts, setUpcomingSportCounts] = useState<
    { sportId: number; count: number }[]
  >([]);

  // A favourite stays in its sport's list as well (its star filled).
  const listToShow =
    selectedLiveFilter === LiveBettingFilters.MY_LIVE_FAVORITES
      ? favorites
      : events;

  const loadUpcomingCount = useCallback(async () => {
    const counts = await Promise.all(
      UPCOMING_DAYS.map(async (day) => {
        const { fromSec, toSec } = buildUpcomingQueryWindow(day);
        const response = await getLiveUpcomingEvents(fromSec, toSec, 1, 1);
        return response?.count ?? null;
      })
    );
    if (counts.some((c) => c === null)) return;
    const [today, tomorrow] = counts as number[];
    setUpcomingDayCounts({ today, tomorrow });
    setUpcomingCount(today + tomorrow);
  }, []);

  const loadUpcoming = useCallback(async () => {
    const { fromSec, toSec } = buildUpcomingQueryWindow(upcomingDay);
    const response = await getLiveUpcomingEvents(
      fromSec,
      toSec,
      upcomingPage,
      UPCOMING_PAGE_SIZE,
      upcomingSport
    );
    if (response == null) {
      return;
    }
    setUpcomingEvents(
      response.events
        .filter(filterUpcomingByKickoff)
        .map(matchOfferToLiveBettingRow)
    );
    const dayTotal = response.sportCounts.reduce((n, x) => n + x.count, 0);
    setUpcomingDayCounts((prev) => ({ ...prev, [upcomingDay]: dayTotal }));
    setUpcomingSportCounts(
      [...response.sportCounts].sort(
        (x, y) =>
          sportChipRank(x.sportId) - sportChipRank(y.sportId) ||
          y.count - x.count
      )
    );
    setUpcomingTotalPages(Math.max(1, response.totalPages));
  }, [upcomingPage, upcomingDay, upcomingSport]);

  useEffect(() => {
    if (selectedLiveFilter === LiveBettingFilters.UPCOMMING_EVENTS) {
      setUpcomingPage(1);
    }
  }, [selectedLiveFilter, upcomingDay, upcomingSport]);

  // A sport with nothing on the chosen day falls back to every sport.
  useEffect(() => {
    if (
      upcomingSport !== 0 &&
      upcomingSportCounts.length > 0 &&
      !upcomingSportCounts.some((x) => x.sportId === upcomingSport)
    ) {
      setUpcomingSport(0);
    }
  }, [upcomingSport, upcomingSportCounts]);

  useEffect(() => {
    loadUpcomingCount();
  }, [loadUpcomingCount]);

  useEffect(() => {
    loadUpcomingCount();
  }, [lastPrematchPush, loadUpcomingCount]);

  useEffect(() => {
    if (selectedLiveFilter !== LiveBettingFilters.UPCOMMING_EVENTS) {
      return;
    }
    loadUpcoming();
  }, [selectedLiveFilter, lastPrematchPush, loadUpcoming, upcomingPage]);

  useEffect(() => {
    if (selectedLiveFilter !== LiveBettingFilters.UPCOMMING_EVENTS) {
      return;
    }
    const id = window.setInterval(() => {
      loadUpcoming();
      loadUpcomingCount();
    }, 30_000);
    return () => window.clearInterval(id);
  }, [selectedLiveFilter, loadUpcoming, loadUpcomingCount]);

  const upcomingPageSafe = Math.min(
    Math.max(1, upcomingPage),
    Math.max(1, upcomingTotalPages)
  );

  return (
    <div className={styles.pageContainer}>
      <div>
        <span className={styles.pageTitle}>{t('live_betting_label')}</span>
        <div className={styles.filterWrapper}>
          <div
            onClick={() =>
              setSelectedLiveFilter(LiveBettingFilters.LIVE_EVENTS)
            }
            className={
              selectedLiveFilter === LiveBettingFilters.LIVE_EVENTS
                ? styles.selectedFilter
                : ''
            }
          >
            {t(LiveBettingFilters.LIVE_EVENTS)} ({liveEventsCount})
          </div>
          <div
            onClick={() =>
              setSelectedLiveFilter(LiveBettingFilters.UPCOMMING_EVENTS)
            }
            className={
              selectedLiveFilter === LiveBettingFilters.UPCOMMING_EVENTS
                ? styles.selectedFilter
                : ''
            }
          >
            {t(LiveBettingFilters.UPCOMMING_EVENTS)} ({upcomingCount})
          </div>
          <div
            onClick={() =>
              setSelectedLiveFilter(LiveBettingFilters.MY_LIVE_FAVORITES)
            }
            className={
              selectedLiveFilter === LiveBettingFilters.MY_LIVE_FAVORITES
                ? styles.selectedFilter
                : ''
            }
          >
            {t(LiveBettingFilters.MY_LIVE_FAVORITES)} ({favoritesCount})
          </div>
        </div>
        {selectedLiveFilter === LiveBettingFilters.UPCOMMING_EVENTS ? (
          <>
            <div className={styles.dayFilter}>
              {UPCOMING_DAYS.map((day) => (
                <div
                  key={day}
                  onClick={() => setUpcomingDay(day)}
                  className={upcomingDay === day ? styles.selectedFilter : ''}
                >
                  {t(UPCOMING_DAY_LABELS[day])} ({upcomingDayCounts[day]})
                </div>
              ))}
            </div>
            <div className={styles.sportFilter}>
              <button
                type="button"
                onClick={() => setUpcomingSport(0)}
                className={upcomingSport === 0 ? styles.selectedSport : ''}
              >
                {t('results_filter_status_all')} (
                {upcomingDayCounts[upcomingDay]})
              </button>
              {upcomingSportCounts.map(({ sportId, count }) => (
                <button
                  type="button"
                  key={sportId}
                  onClick={() => setUpcomingSport(sportId)}
                  className={
                    upcomingSport === sportId ? styles.selectedSport : ''
                  }
                >
                  <SportIcon sportId={sportId} alt="" tone="chrome" />
                  {t(sportTranslationKey(sportId), {
                    defaultValue: getLiveBettingSportName(sportId),
                  })}{' '}
                  ({count})
                </button>
              ))}
            </div>
            <LiveSportEvents
              sport={{
                id: SportIDs.SOCCER,
                name: t('upcomming_events_label'),
                count: Math.min(UPCOMING_PAGE_SIZE, upcomingEvents.length),
              }}
              events={upcomingEvents}
              titleOverride={t('upcomming_events_label')}
              labelsOverride={[
                'tip',
                'ht_set_third',
                'next_goal',
                'over_under',
              ]}
              iconOverrideKey="clock"
              hideChevron
              prematchUpcoming
            />
            {upcomingTotalPages > 1 && (
              <div className={styles.upcomingPagerRow}>
                <UgPager
                  currentPage={upcomingPageSafe}
                  totalPages={upcomingTotalPages}
                  onPageChange={setUpcomingPage}
                />
              </div>
            )}
          </>
        ) : (
          listToShow &&
          getTotalEelementsCount(listToShow) > 0 &&
          (Array.from(listToShow.keys()) as number[])
            .filter((sportId) => (listToShow.get(sportId) || []).length)
            .map((x) => (
              <LiveSportEvents
                sport={{
                  id: x,
                  name: getLiveBettingSportName(x),
                }}
                events={listToShow.get(x)!}
                key={x}
                favorite={
                  selectedLiveFilter === LiveBettingFilters.MY_LIVE_FAVORITES
                }
              />
            ))
        )}
      </div>
      <div data-slip-column>
        <SportsBettingRightSide slipEntries={entries} />
      </div>
    </div>
  );
};
